import React, { useEffect, useState } from 'react';
import { useNavigate }                from 'react-router-dom';
import { useAuth }                    from '../contexts/AuthContext';
import { getResearched }              from '../api/parts';

export default function ResearchPage() {
  const { user, signOut } = useAuth();
  const navigate          = useNavigate();

  const [vehicle,  setVehicle]  = useState(null);
  const [parts,    setParts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try {
        const v = JSON.parse(cached);
        setVehicle(v);
        fetchResearched(v.vehicleId);
      } catch (_) {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchResearched(vehicleId) {
    setLoading(true);
    setError('');
    try {
      const res = await getResearched(vehicleId);
      if (res.success) {
        setParts(res.data || []);
      } else {
        setError(res.message || 'Could not load researched parts.');
      }
    } catch {
      setError('Could not load researched parts.');
    } finally {
      setLoading(false);
    }
  }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  return (
    <div className="pts-root">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="pts-header">
        <div className="pts-header-brand">
          <span className="pts-header-gear">⚙</span>
          <span className="pts-header-name">AutoRestoration</span>
        </div>

        <div className="pts-header-vehicle">
          {vehicle && (
            <>
              <span className="pts-vehicle-icon">🚗</span>
              <span className="pts-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="pts-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="pts-header-user">
          <span className="pts-username">@{user?.username}</span>
          <button className="pts-signout-btn" onClick={() => { signOut(); navigate('/'); }}>Sign Out</button>
        </div>
      </header>

      {/* ── Page body ───────────────────────────────────────────────── */}
      <div className="pts-body">

        <button className="pts-back-dash" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>

        <div className="pts-page-title-row">
          <div className="pts-breadcrumb">
            <span className="pts-crumb pts-crumb-active">🔬 Research</span>
          </div>
        </div>

        {loading && <div className="pts-loading">Loading researched parts…</div>}

        {!loading && error && <div className="pts-error">{error}</div>}

        {!loading && !error && parts.length === 0 && (
          <div className="pts-empty">
            <div className="pts-empty-icon">🔬</div>
            <h3 className="pts-empty-title">No research yet</h3>
            <p className="pts-empty-text">
              Open a part and use the Research tab to generate AI research notes.
              All researched parts will appear here.
            </p>
          </div>
        )}

        {!loading && parts.length > 0 && (
          <div className="pts-parts-section">
            <div className="pts-parts-header-row">
              <span className="pts-parts-count">
                {parts.length} researched part{parts.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="pts-parts-list">
              {parts.map(p => (
                <div
                  key={p.partId}
                  className="pts-part-card pts-part-card-clickable"
                  onClick={() => navigate(`/parts/${p.partId}`, { state: { from: 'research' } })}
                >
                  <div className="pts-part-main">
                    <span className="pts-part-name">{p.part_name}</span>
                    {p.oem_part_number && (
                      <span className="pts-part-oem">OEM# {p.oem_part_number}</span>
                    )}
                    <span className="pts-part-detail-hint">›</span>
                  </div>
                  <div className="pts-part-meta">
                    <span className="pts-part-condition" style={{ background: '#2a4a7a' }}>
                      {p.category_name}
                    </span>
                    {p.researched_at && (
                      <span className="pts-part-flag" title="Research date">
                        📅 {p.researched_at}
                      </span>
                    )}
                  </div>
                  {p.research_summary && (
                    <div className="pts-part-research-snippet">
                      {p.research_summary.length > 180
                        ? p.research_summary.slice(0, 180).trim() + '…'
                        : p.research_summary}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
